<template>
  <div class="tile-details" @drop="onTileDrop" @dragover="onTileDragOver">
    <p class="index">{{ index }}</p>
    <button type="button" @click="deleteTile" class="delete-tile-btn" title="Delete tile">
      <span class="sr-only">Delete tile</span
      ><span class="material-icons" aria-hidden="true">
        clear
      </span>
    </button>
    <div class="form-row">
      <label :for="`originalImageFilename${index}`">Original image filename</label>
      <input :id="`originalImageFilename${index}`" type="text" v-model="originalImageFilename" readonly />
    </div>
    <div class="form-row">
      <label :for="`tileId${index}`">Tile ID <span class="required-asterisk" aria-hidden="true" title="Required">*</span></label>
      <input :id="`tileId${index}`" type="text" v-model="tileId" required readonly />
    </div>
    <div class="form-row">
      <label :for="`imageFilename${index}`">Image filename</label>
      <input :id="`imageFilename${index}`" type="text" v-model="imageFilename" />
    </div>
    <div class="form-row">
      <label :for="`prompt${index}`">Prompt <span class="required-asterisk" aria-hidden="true" title="Required">*</span></label>
      <input :id="`prompt${index}`" type="text" v-model="prompt" required @paste="onPromptPaste" @change="onPromptChange" @keyup="onPromptKeyup" />
    </div>
    <div class="form-row">
      <label :for="`answerText${index}`">Answer text</label>
      <input :id="`answerText${index}`" type="text" v-model="answerText" />
    </div>
    <div class="form-row">
      <label :for="`backgroundColor${index}`">Background colour</label>
      <div class="form-row__inner-row">
        <input :id="`backgroundColor${index}`" type="text" v-model="backgroundColor" />
        <label :for="`backgroundColorPicker${index}`" class="color-picker__label" :style="backgroundColorPickerLabelInlineStyle"><span class="sr-only">Colour picker</span></label>
        <input :id="`backgroundColorPicker${index}`" type="color" v-model="backgroundColor" class="color-picker__input sr-only" />
      </div>
    </div>
    <div class="form-row">
      <label :for="`radialGradientHexColor${index}`">Radial gradient hex colour</label>
      <div class="form-row__inner-row">
        <input :id="`radialGradientHexColor${index}`" type="text" v-model="radialGradientHexColor" />
        <label :for="`radialGradientHexColorPicker${index}`" class="color-picker__label" :style="radialGradientColorPickerLabelInlineStyle"
          ><span class="sr-only">Colour picker</span></label
        >
        <input :id="`radialGradientHexColorPicker${index}`" type="color" v-model="radialGradientHexColor" class="color-picker__input sr-only" />
      </div>
    </div>
    <div class="form-row form-row--last">
      <label class="control control--checkbox">
        Enabled
        <input type="checkbox" v-model="enabled" />
        <div class="control__indicator"></div>
      </label>
    </div>
  </div>
</template>

<script>
import filenameToPromptString from './../helpers/filenameToPromptString';
import {DEFAULT_TILE_BACKGROUND_COLOR, DEFAULT_TILE_RADIAL_GRADIENT_HEX_COLOR} from './../constants/globalConstants';

export default {
  name: 'EditBoardTile',
  props: {
    index: Number,
    tileObject: Object,
  },
  data() {
    return {
      answerText: '',
      backgroundColor: DEFAULT_TILE_BACKGROUND_COLOR,
      enabled: true,
      imageFilename: '',
      originalImageFilename: '',
      prompt: '',
      radialGradientHexColor: DEFAULT_TILE_RADIAL_GRADIENT_HEX_COLOR,
      tileId: '',
    };
  },
  watch: {
    answerText: function() {
      this.tileObject.answerText = this.answerText;
    },
    backgroundColor: function() {
      this.tileObject.backgroundColor = this.backgroundColor;
    },
    enabled: function() {
      this.tileObject.enabled = this.enabled;
    },
    imageFilename: function() {
      this.tileObject.imageFilename = this.imageFilename;
    },
    prompt: function() {
      this.tileObject.prompt = this.prompt;
    },
    radialGradientHexColor: function() {
      this.tileObject.radialGradientHexColor = this.radialGradientHexColor;
    },
    tileId: function() {
      this.tileObject.tileId = this.tileId;
    },
  },
  created: function() {
    this.answerText = this.tileObject.answerText;
    this.backgroundColor = this.tileObject.backgroundColor;
    this.enabled = this.tileObject.enabled;
    this.imageFilename = this.tileObject.imageFilename;
    this.originalImageFilename = this.tileObject.originalImageFilename;
    this.prompt = this.tileObject.prompt;
    this.radialGradientHexColor = this.tileObject.radialGradientHexColor;
    this.tileId = this.tileObject.tileId;
  },
  computed: {
    backgroundColorPickerLabelInlineStyle: function() {
      if (this.backgroundColor) {
        return `background-color: ${this.backgroundColor}; border: 0;`;
      } else {
        return '';
      }
    },
    radialGradientColorPickerLabelInlineStyle: function() {
      if (this.radialGradientHexColor) {
        return `background-color: ${this.radialGradientHexColor}; border: 0;`;
      } else {
        return '';
      }
    },
  },
  methods: {
    deleteTile: function() {
      this.$emit('delete-tile', this.index);
    },
    onPromptKeyup: function() {
      this.removeUnderscoresFromPrompt();
    },
    onPromptPaste: function() {
      this.removeUnderscoresFromPrompt();
    },
    onPromptChange: function() {
      this.removeUnderscoresFromPrompt();
    },
    removeUnderscoresFromPrompt: function() {
      this.prompt = this.prompt.replace(/_/g, ' ');
    },
    onTileDrop: function(evt) {
      evt.preventDefault();
      const filename = evt.dataTransfer.items[0].getAsFile().name;
      this.prompt = filenameToPromptString(filename);
    },
    onTileDragOver: function(evt) {
      evt.preventDefault();
    },
    extensionChanged: function(newExtension) {
      let imageFilename = this.imageFilename.trim();
      const positionOfLastDot = imageFilename.lastIndexOf('.');
      if (positionOfLastDot > -1) {
        imageFilename = `${imageFilename.substr(0, positionOfLastDot)}${newExtension}`;
      } else {
        imageFilename = `${imageFilename}${newExtension}`;
      }
      this.imageFilename = imageFilename;
    },
  },
};
</script>

<style lang="scss" scoped>
.tile-details {
  background-color: var(--text-color-alpha-5);
  border: 1px solid var(--text-color-alpha-10);
  border-radius: var(--border-radius);
  padding: 2rem;
  position: relative;
}

.index {
  color: var(--text-color-alpha-30);
  display: inline-block;
  font-size: 1.2rem;
  left: 6px;
  line-height: 1;
  margin: 0;
  position: absolute;
  top: 6px;
}

.delete-tile-btn {
  border-radius: 50%;
  height: 2.4rem;
  line-height: 1;
  margin: 0;
  padding: 0;
  position: absolute;
  right: -0.7rem;
  top: -0.7rem;
  width: 2.4rem;
}

.form-row {
  display: flex;
  flex-direction: column;

  @include breakpoint($mq-sm-md) {
    align-items: center;
    flex-direction: row;
  }

  label {
    &:not(.color-picker__label) {
      flex-shrink: 0;
      margin-right: 1rem;
      min-width: 20rem;

      @include breakpoint($mq-sm-md) {
        margin-bottom: 0;
      }
    }

    &.control {
      &--checkbox {
        @include breakpoint($mq-sm-md) {
          padding-left: 0;
        }

        .control__indicator {
          left: auto;
          right: -3rem;
        }
      }
    }
  }

  &__inner-row {
    display: flex;
    flex-direction: row;
    width: 100%;
  }
}

.color-picker__label {
  align-items: center;
  border: 1px solid var(--color-picker-label-border);
  border-radius: var(--border-radius);
  cursor: pointer;
  display: flex;
  flex-basis: 0;
  justify-content: center;
  margin-bottom: 0;
  margin-left: 1rem;
  min-width: 5rem;
  width: 5rem;
}

.color-picker__input {
  height: 4rem;
  padding: 0;
}
</style>
